import {useFormikContext} from 'formik';
import React, {useCallback} from 'react';
import {Col, Form, Row, Image} from 'react-bootstrap';
import {Dropdown} from 'shared/components/Forms/Dropdown';
import {useTabletSize} from 'shared/helpers/DeviceSize';

type OptionsType = {
    image?: string;
    description?: string;
    hidden?: boolean;
    name?: string;
};

type BooleanType = {
    name: string;
    value: boolean | number;
    setFieldValue: (name: string, value: boolean | number) => void;
    type?: string;
    alignRight?: boolean;
    disabled?: boolean;
    disabledMessage?: () => void | string;
    yesOptions?: OptionsType;
    noOptions?: OptionsType;
};

export const Boolean = ({
    name,
    value,
    setFieldValue,
    yesOptions,
    noOptions,
    type = 'radio',
    alignRight = false,
    disabled = false,
    disabledMessage,
}: BooleanType) => {
    const isTabletSize = useTabletSize();
    value = value ? value : type === 'radio' ? false : 0;
    const formikContext = useFormikContext();

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const targetValue = event.target.value == 'true';
            setFieldValue(name, targetValue);

            if (formikContext) {
                void formikContext.setFieldTouched(name, true);
            }
        },
        [name, formikContext]
    );

    const handleCheck = useCallback(
        (name: string) => () => {
            if (disabled && disabledMessage) {
                disabledMessage();
            }
            // click radio button on click of image/description
            const element = document.getElementById(name);

            if (element) {
                element.click();
            }
        },
        [disabled, disabledMessage]
    );

    const handleChangeCheckbox = useCallback(() => {
        setFieldValue(name, !value);

        if (formikContext) {
            void formikContext.setFieldTouched(name, true);
        }
    }, [name, value, setFieldValue, formikContext]);

    const opacity = {
        opacity: '0.5',
    };

    if (type === 'radio') {
        return (
            <Row>
                {yesOptions &&
                (!yesOptions.hasOwnProperty('hidden') || !yesOptions.hidden) ? (
                    <Col md={6} xs={6}>
                        <div
                            className="cbc-radio"
                            onClick={handleCheck(`boolean_${name}_yes`)}
                            style={!value ? opacity : null}>
                            {yesOptions.image || yesOptions.description ? (
                                <section
                                    style={{
                                        display: 'flex',
                                        padding: '10px 0',
                                        flexWrap: 'wrap',
                                    }}>
                                    {yesOptions.image ? (
                                        <section
                                            style={{
                                                marginRight: '10px',
                                                cursor: 'pointer',
                                                width: '150px',
                                            }}>
                                            <Image
                                                src={`/${yesOptions.image}`}
                                                thumbnail
                                            />
                                        </section>
                                    ) : null}
                                    {yesOptions.description ? (
                                        <section
                                            style={{
                                                fontSize: '.75em',
                                                textAlign: 'justify',
                                                flex: isTabletSize
                                                    ? 'unset'
                                                    : 1,
                                            }}>
                                            {yesOptions.description}
                                        </section>
                                    ) : null}
                                </section>
                            ) : null}
                            <Form.Check
                                type="radio"
                                id={`boolean_${name}_yes`}
                                name={name}
                                disabled={disabled}
                                checked={!!value}
                                label={
                                    yesOptions.name ? yesOptions.name : 'Yes'
                                }
                                onChange={handleChange}
                            />
                        </div>
                    </Col>
                ) : null}
                {noOptions &&
                (!noOptions.hasOwnProperty('hidden') || !noOptions.hidden) ? (
                    <Col md={6} xs={6}>
                        <div
                            className="cbc-radio"
                            onClick={handleCheck(`boolean_${name}_no`)}
                            style={value ? opacity : null}>
                            {noOptions.image || noOptions.description ? (
                                <section
                                    style={{
                                        display: 'flex',
                                        padding: '10px 0',
                                        flexWrap: 'wrap',
                                    }}>
                                    {noOptions.image ? (
                                        <section
                                            style={{
                                                marginRight: '10px',
                                                cursor: 'pointer',
                                                width: '150px',
                                            }}>
                                            <Image
                                                src={`/${noOptions.image}`}
                                                thumbnail
                                            />
                                        </section>
                                    ) : null}
                                    {noOptions.description ? (
                                        <section
                                            style={{
                                                fontSize: '.75em',
                                                textAlign: 'justify',
                                                flex: isTabletSize
                                                    ? 'unset'
                                                    : 1,
                                            }}>
                                            {noOptions.description}
                                        </section>
                                    ) : null}
                                </section>
                            ) : null}
                            <Form.Check
                                type="radio"
                                id={`boolean_${name}_no`}
                                name={name}
                                disabled={disabled}
                                checked={!value}
                                label={noOptions.name ? noOptions.name : 'No'}
                                onChange={handleChange}
                            />
                        </div>
                    </Col>
                ) : null}
            </Row>
        );
    } else if (type === 'checkbox') {
        return (
            <Row>
                <Col>
                    <div style={alignRight ? {textAlign: 'right'} : {}}>
                        <Form.Check
                            type="switch"
                            id={`boolean_${name}_no`}
                            label=""
                            checked={!!value}
                            onChange={handleChangeCheckbox}
                        />
                    </div>
                </Col>
            </Row>
        );
    } else {
        const options = [
            {
                id: 1,
                name: yesOptions && yesOptions.name ? yesOptions.name : 'Yes',
                value: 1,
            },
            {
                id: 0,
                name: noOptions && noOptions.name ? noOptions.name : 'No',
                value: 0,
            },
        ];

        return (
            <Dropdown
                options={options}
                name={name}
                selectHandler={setFieldValue}
                value={value ? 1 : 0}
            />
        );
    }
};
